import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, HashRouter } from 'react-router-dom';

import '@supernova/design-system/tokens.css';

import { App } from './App.js';
import { ThemedProvider } from './components/ThemedProvider.js';
import { MantenimientoGate } from './components/MantenimientoGate.js';
import { SesionCerradaGate } from './components/SesionCerradaGate.js';
import { DEMO, DEMO_SESSION } from './demo/index.js';
import { DemoMockBanner } from './demo/DemoMockBanner.js';
import { AsistenteEcosoft } from './demo/AsistenteEcosoft.js';
import { useAuthStore } from './stores/authStore.js';
import { useThemeStore } from './stores/themeStore.js';
import { aplicarBrandingDelProducto } from './lib/brandingDelProducto.js';
import { embebidoEnHub } from './lib/embebidoEnHub.js';
import { EnlacePublicoPage } from './routes/EnlacePublicoPage.js';

// MODO DEMO ("DEMO OPUS ai"): auto-login como visitante de demostración,
// tema oscuro, y ruteo por hash (#) para que el .html single-file funcione desde
// file:// (doble clic) o colgado en cualquier carpeta (opus.com.mx/demo-ai/).
if (DEMO) {
  useAuthStore.getState().setSession(DEMO_SESSION);
  useThemeStore.getState().setMode('dark');
  document.title = 'DEMO OPUS ai';
} else {
  // Favicon + título + theme-color según el producto del hostname (ai / ci).
  aplicarBrandingDelProducto();
  // ¿Dentro del hub? Se decide YA, antes de que el router quite el ?embed=hub de la URL.
  embebidoEnHub();
}
const Router = DEMO ? HashRouter : BrowserRouter;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('#root not found');

// «Enlace para tu cliente»: página pública, SIN sesión. Va ANTES del arranque de sesión (AuthBootstrap, los
// vigilantes de App y el asistente): quien abre el enlace no tiene cuenta y esos efectos llamarían a la API.
const esEnlacePublico = window.location.pathname.replace(/\/+$/, '') === '/compartido';

createRoot(rootEl).render(
  esEnlacePublico ? (
    <StrictMode>
      <ThemedProvider>
        <EnlacePublicoPage />
      </ThemedProvider>
    </StrictMode>
  ) : (
  <StrictMode>
    <ThemedProvider>
      <QueryClientProvider client={queryClient}>
        {/* Va ACÁ, por encima del arranque de sesión (AuthBootstrap vive dentro de
            <App />), y no adentro: durante un despliegue el motor tampoco puede
            decir quién es el usuario, así que el arranque falla y la aplicación
            cae en la pantalla de inicio de sesión. Desde acá, quien abra la
            aplicación en plena ventana ve "estamos actualizando", que es la
            verdad, y no una invitación a intentar entrar. */}
        <MantenimientoGate />
        {/* Igual que el de arriba: encima de todo, para que el aviso de sesión
            cerrada no dependa de en qué pantalla estaba la persona. Se muestra
            solo si la sesión estaba realmente abierta (lo decide él). */}
        <SesionCerradaGate />
        {/* Identidad de la demostración: listón "DEMO OPUS ai" + aviso de entrada. */}
        {DEMO && <DemoMockBanner />}
        <Router>
          <App />
          {/* Asistente Ecosoft (logo animado): guía el recorrido. Necesita el Router. */}
          {DEMO && <AsistenteEcosoft />}
        </Router>
      </QueryClientProvider>
    </ThemedProvider>
  </StrictMode>
  ),
);
